/** @jsxImportSource @emotion/react */
import { faFlashlight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { css } from "@emotion/react";
import React, { useCallback, useMemo, useState } from "react";
import Select from "react-select";
import Webcam from "react-webcam";
import {
    Button,
    Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";
import { useCameraDevice } from "../../../../../hooks/useCameraDevice";
import { toast } from "../../../../../utils/toast";
import { checkImageSizeAndShrinkIfNeeded } from "../../../image-utils";

interface CameraModalProps {
    requiredImage: any;
    onClose: () => void;
    onSaveImage: (imageType: string, base64: string, focusMode: any) => Promise<any>;
    onOpenFiles: () => void;
}

export function CameraModal({ requiredImage, onClose, onSaveImage, onOpenFiles }: CameraModalProps) {
    const webcamRef = React.useRef<Webcam | null>(null);
    const [focusMode, setFocusMode] = useState({
        browser: [], capabilities: [], constraint: "", user_agent: "",
    });

    const {
        devices,
        selectedDeviceId,
        selectedDeviceIdExists,
        torchEnabled,
        toggleTorch,
        setCameraDevice,
        onDevicesLoaded,
        visibleCameraOptions,
    } = useCameraDevice();

    const videoConstraints = useMemo(() => ({
        facingMode: "environment",
        focusMode: "continuous",
        width: 960,
        height: 720,
        torch: torchEnabled,
        ...(selectedDeviceIdExists && selectedDeviceId
            ? { deviceId: { exact: selectedDeviceId } }
            : { zoom: 1.0 }),
    }), [selectedDeviceId, selectedDeviceIdExists, torchEnabled]);

    const captureAndContinue = async () => {
        const { video } = webcamRef.current!;
        if (video) {
            const base64 = webcamRef.current!.getScreenshot()!;
            const resizedBase64 = await checkImageSizeAndShrinkIfNeeded(base64, video.videoWidth, video.videoHeight);

            if (!resizedBase64) {
                console.log("Error video stream is not initialized"); // eslint-disable-line no-console
                return;
            }

            onSaveImage(requiredImage.type, resizedBase64, focusMode).then(() => {
                toast.success(
                    <div className="">
                        <div className="mb-2"><b>Photo successfully saved.</b></div>
                        <img
                            src={resizedBase64}
                            alt="Preview"
                            css={css`width: 160px; height: 120px; object-fit: cover; border-radius: 4px; margin-right: 12px;`}
                        />
                    </div>,
                    {
                        autoClose: 3500,
                    }
                );
            }).catch(() => {
                toast.error(`Failed to save Image`, {
                    autoClose: 3500,
                });
            });
        }
    };

    const onCameraError = (error) => {
        toast.error(`Failed to start camera: (${error.name}:${error.message}). Please stop any application that is using your camera.`, {
            // toastId: "create-location",
            autoClose: 3500,
        });
    };

    const logCapabilities = useCallback((media) => {
        const SupportedConstraints = navigator.mediaDevices.getSupportedConstraints();
        const focusModeStatus = {
            browser: (SupportedConstraints as any).focusMode,
            capabilities: [],
            constraint: "",
            user_agent: navigator.userAgent,
        };
        if (media.getVideoTracks) {
            const tracks = media.getVideoTracks();
            if (tracks && tracks.length > 0) {
                const track = tracks[0];
                if (track.getCapabilities) {
                    const capabilities = track.getCapabilities();
                    focusModeStatus.capabilities = capabilities.focusMode;
                }
                if (track.getSettings) {
                    const settings = track.getSettings();
                    focusModeStatus.constraint = settings.focusMode || "";
                }
                setFocusMode(focusModeStatus);
            }
        }

        // Enumerate available video devices; clear stale stored device if it no longer exists
        navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
            onDevicesLoaded(mediaDevices.filter((d) => d.kind === "videoinput"));
        });
    }, [onDevicesLoaded]);

    return (
        <Modal isOpen={true} className="modal-xl" fullscreen={true}>
            <ModalHeader toggle={onClose}>{requiredImage.label}</ModalHeader>
            <ModalBody>
                <div className="d-flex align-items-center justify-content-center h-100">
                    <Webcam
                        onClick={captureAndContinue}
                        onUserMediaError={onCameraError}
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/webp"
                        forceScreenshotSourceSize={true}
                        screenshotQuality={0.96} // default is 0.92
                        onUserMedia={logCapabilities}
                        imageSmoothing={true}
                        videoConstraints={videoConstraints}
                        className="h-100 w-100"

                    />
                </div>
            </ModalBody>
            <ModalFooter css={css`display: flex; justify-content: space-between; align-items: center;`}>
                <div css={css`flex: 1; max-width: 300px;`}>
                    {devices.length > 1 && (
                        <Select
                            options={visibleCameraOptions}
                            value={visibleCameraOptions.find((o) => o.value === selectedDeviceId) || null}
                            onChange={(option) => setCameraDevice("camera", option?.value || null)}
                            isClearable={false}
                        />
                    )}
                </div>
                <div>
                    <Button color={torchEnabled ? "warning" : "secondary"} className="me-2" onClick={toggleTorch} aria-label="Flash">
                        <FontAwesomeIcon icon={faFlashlight} />
                    </Button>
                    <Button color="secondary" className="me-2" onClick={onOpenFiles}>Go to files</Button>
                    <Button color="primary" onClick={captureAndContinue}>Capture</Button>
                </div>
            </ModalFooter>
        </Modal>
    );
}

